<template>
  <v-btn
    large
    outlined
    color="red darken-2"
    :loading="isExporting"
    :disabled="isExporting"
    @click="exportPDF"
  >
    <v-icon
      left
      color="red darken-2"
    >
      mdi-file-pdf-box
    </v-icon>

    Export Performance Report

    <template #loader>
      <v-progress-circular
        indeterminate
        size="20"
        width="2"
        color="red darken-2"
      />
    </template>
  </v-btn>
</template>

<script>
import {
  buildKdRows,
  buildPerformanceReportDocDefinition,
} from "./export2";

import { mapState, mapGetters, mapActions } from "vuex";
import { ProfilesService } from "../../../services/profiles/service";
import { kdProfileDataFromJson } from "../../../domain/kdiagram/profiles/mapper";
import {
  kdProfileCreateDataToJson,
  kdProfileDataToJson,
} from "../../../domain/kdiagram/profiles/mapper_to_json";
import redirectInterceptor from "@/interceptors/redirect_interceptor";

import { getConfig } from "@/config";

export default {
  name: "ExportPerformanceReport",

  data() {
    return {
      isExporting: false,
    };
  },

  computed: {
    ...mapState("vesselInformation", ["selectedLeg"]),
    ...mapState("cii", ["ciiHistory"]),
    ...mapState("kDiagram", ["calculationResult"]),

    ...mapGetters("vesselInformation/voyageModule", [
      "displayDeparturePort",
      "displayArrivalPort",
      "displayDepartureTime",
      "displayHfo",
      "displayMgo",
      "displayLfo",
      "displayTotalDistRun",
    ]),

    ...mapGetters("vesselInformation/foulingModule", ["displayFouling"]),

    ...mapGetters("vesselInformation/overconsumptionModule", [
      "displayOverconsumption",
      "displayCompleteness",
    ]),

    ...mapGetters(["getVesselFouling", "getVesselFoulingCons"]),

    isCompany5() {
      return process.env.VUE_APP_COMPANY_ID === "5";
    },

    hfoLabel() {
      return this.isCompany5 ? "HSFO" : "HFO";
    },

    lfoLabel() {
      return this.isCompany5 ? "VLSFO" : "LFO";
    },
  },

  methods: {
    ...mapActions(["fetchVesselFouling", "fetchVesselFoulingCons"]),

    formatNumber(value, decimals = 2) {
      if (value === null || value === undefined || value === "-") {
        return "-";
      }

      const num = Number(value);

      if (isNaN(num)) {
        return "-";
      }

      if (Math.abs(num) < 0.001) {
        return (0).toFixed(decimals);
      }

      return num.toFixed(decimals);
    },

    calculateAverageWindBft(voyageWindData) {
      if (!Array.isArray(voyageWindData) || voyageWindData.length === 0) {
        return "-";
      }

      let weightedSum = 0;
      let totalPercentage = 0;

      voyageWindData.forEach((series) => {
        const bftMatch = String(series.name || "").match(/(\d+(?:\.\d+)?)\s*BFT/i);

        if (!bftMatch || !Array.isArray(series.data)) {
          return;
        }

        const bftValue = Number(bftMatch[1]);

        series.data.forEach((point) => {
          const percentage = Array.isArray(point)
            ? Number(point[1])
            : Number(point);

          if (!isNaN(percentage)) {
            weightedSum += bftValue * percentage;
            totalPercentage += percentage;
          }
        });
      });

      if (totalPercentage === 0) {
        return "-";
      }

      return `${(weightedSum / totalPercentage).toFixed(2)} BFT`;
    },

    getGeneralWeatherDescription(avgWindSpeed) {
      if (!avgWindSpeed || avgWindSpeed === "-") {
        return "-";
      }

      const bft = Number(String(avgWindSpeed).replace("BFT", "").trim());

      if (isNaN(bft)) {
        return "-";
      }

      if (bft < 3) {
        return "Good";
      }

      if (bft >= 3 && bft < 5) {
        return "Mild";
      }

      return "Bad";
    },

    async fetchAverageWindBft(vessel, startDate, stopDate) {
      try {
        const baseUrl = getConfig().VUE_APP_BASIC_URL;

        const url = `${baseUrl}/vesselInformation/${vessel}/voyageRelativeWindData`;

        const response = await redirectInterceptor.get(url, {
          params: {
            startDate,
            stopDate,
          },
          headers: {
            Authorization: localStorage.getItem("quad_token"),
          },
        });

        if (response.status !== 200) {
          return "-";
        }

        return this.calculateAverageWindBft(response.data);
      } catch (error) {
        return "-";
      }
    },

    async getPdfMake() {
      const pdfMakeModule = await import("pdfmake/build/pdfmake");
      const pdfFontsModule = await import("pdfmake/build/vfs_fonts");

      const pdfMake = pdfMakeModule.default || pdfMakeModule;
      const pdfFonts = pdfFontsModule.default || pdfFontsModule;

      if (pdfFonts.pdfMake && pdfFonts.pdfMake.vfs) {
        pdfMake.vfs = pdfFonts.pdfMake.vfs;
      } else if (pdfFonts.vfs) {
        pdfMake.vfs = pdfFonts.vfs;
      }

      return pdfMake;
    },

    safeFilenameValue(value) {
      return String(value || "-")
        .replace(/[\\/:*?"<>|]/g, "")
        .replace(/\s+/g, "-");
    },

    async fetchReportBaseData(vessel, startDate, stopDate) {
      await Promise.all([
        this.$store.dispatch("vesselInformation/fetchVoyageData", vessel),
        this.$store.dispatch("cii/fetchCiiHistory", vessel),
        this.fetchVesselFouling(vessel),
        this.fetchVesselFoulingCons(vessel),
        this.$store.dispatch("sensorIssues/fetchSensorIssues", {
          name: vessel,
          startDate,
          stopDate,
        }),
      ]);
    },

    async fetchKdProfiles(vessel) {
      const profilesService = new ProfilesService(
        localStorage.getItem("basicUrl"),
        kdProfileDataFromJson,
        kdProfileCreateDataToJson,
        kdProfileDataToJson
      );

      try {
        const profiles = await profilesService.getMany(
          localStorage.getItem("quad_token"),
          vessel
        );

        const ladenProfile =
          profiles.find(
            (profile) =>
              String(profile.profileName || "").trim().toLowerCase() === "laden"
          ) || null;

        const ballastProfile =
          profiles.find(
            (profile) =>
              String(profile.profileName || "").trim().toLowerCase() === "ballast"
          ) || null;

        return {
          ladenProfile,
          ballastProfile,
        };
      } catch (error) {
        console.error("Failed to fetch K-Diagram profiles:", error);

        return {
          ladenProfile: null,
          ballastProfile: null,
        };
      }
    },

    async calculateKdResult(vessel, profile) {
      if (!profile) {
        return null;
      }

      const result = await this.$store.dispatch("kDiagram/calculate", {
        vessel,
        meanDraft: Number(Number(profile.draft).toFixed(2)),
        trim: Number(Number(profile.trim).toFixed(3)),
        trueWindSpeed: 2,
      });

      return JSON.parse(JSON.stringify(result));
    },

    getLatestCleaningDate() {
      if (
        !this.getVesselFouling ||
        !this.getVesselFouling.log ||
        this.getVesselFouling.log.length === 0
      ) {
        return "-";
      }

      const latestFoulingEvent =
        this.getVesselFouling.log
          .filter((event) => event && event.Date)
          .sort((a, b) => new Date(b.Date) - new Date(a.Date))[0] || null;

      return latestFoulingEvent && latestFoulingEvent.Date
        ? latestFoulingEvent.Date.substring(0, 10)
        : "-";
    },

    getSensorIssues() {
      const sensorIssuesModule = this.$store.state.sensorIssues;

      return sensorIssuesModule && sensorIssuesModule.sensorIssues
        ? sensorIssuesModule.sensorIssues
        : [];
    },

    async fetchAverageWaveHeight(vessel, startDate, stopDate) {
      try {
        const baseUrl = getConfig().VUE_APP_BASIC_URL;

        const url = `${baseUrl}/vesselInformation/${vessel}/wavelength`;

        const response = await redirectInterceptor.get(url, {
          params: {
            startDate,
            stopDate,
          },
          headers: {
            Authorization: localStorage.getItem("quad_token"),
          },
        });

        if (response.status !== 200) {
          return "-";
        }

        const payload =
      response.data && response.data.data
        ? response.data.data
        : response.data;

        if (Array.isArray(payload)) {
          return this.calculateAverageWaveHeight(payload);
        }

        return "-";
      } catch (error) {
        console.error("Failed to fetch average wave height:", error);
        return "-";
      }
    },

    calculateAverageWaveHeight(waveData) {
      if (!Array.isArray(waveData) || waveData.length === 0) {
        return "-";
      }

      const values = waveData
        .map((item) => {
          if (typeof item === "number") {
            return item;
          }

          if (!item) {
            return NaN;
          }

          if (
            item.maximumWaveHeight !== undefined &&
        item.maximumWaveHeight !== null
          ) {
            return Number(item.maximumWaveHeight);
          }

          if (item.waveHeight !== undefined && item.waveHeight !== null) {
            return Number(item.waveHeight);
          }

          if (item.wave_height !== undefined && item.wave_height !== null) {
            return Number(item.wave_height);
          }

          if (item.value !== undefined && item.value !== null) {
            return Number(item.value);
          }

          return NaN;
        })
        .filter((value) => !isNaN(value));

      if (values.length === 0) {
        return "-";
      }

      const average =
    values.reduce((sum, value) => sum + value, 0) / values.length;

      return `${this.formatNumber(average, 2)} m`;
    },
    async exportPDF() {
      try {
        this.isExporting = true;

        const pdfMake = await this.getPdfMake();

        const vessel = this.$route.params.vessel || "Unknown Vessel";
        const generatedAt = new Date();
        const today = generatedAt.toISOString().split("T")[0];

        const startDate =
          this.selectedLeg && this.selectedLeg.startDate
            ? this.selectedLeg.startDate.substring(0, 10)
            : "-";

        const stopDate =
          this.selectedLeg && this.selectedLeg.stopDate
            ? this.selectedLeg.stopDate.substring(0, 10)
            : "-";

        const reportTitle = "Vessel Performance Report";
        const reportSubject =
          "Voyage Performance, CII, K-Diagram and Fouling Assessment";
        const reportAuthor = "QUAD";

        await this.fetchReportBaseData(vessel, startDate, stopDate);

        const avgWindSpeed = await this.fetchAverageWindBft(
          vessel,
          startDate,
          stopDate
        );

        const avgWaveHeight = await this.fetchAverageWaveHeight(
          vessel,
          startDate,
          stopDate
        );

        const genWeatherDesc = this.getGeneralWeatherDescription(avgWindSpeed);

        this.$store.commit("kDiagram/SET_DATE_RANGE", [startDate, stopDate]);

        const { ladenProfile, ballastProfile } = await this.fetchKdProfiles(vessel);

        const [ladenResult, ballastResult] = await Promise.all([
          this.calculateKdResult(vessel, ladenProfile),
          this.calculateKdResult(vessel, ballastProfile),
        ]);

        const voyageNum = this.selectedLeg ? this.selectedLeg.currentNum : "-";
        const voyageLeg = this.selectedLeg ? this.selectedLeg.currentLeg : "-";

        const departurePort = this.displayDeparturePort || "-";
        const arrivalPort = this.displayArrivalPort || "-";
        const departureTime = this.displayDepartureTime || "-";
        const totalDistRun = this.displayTotalDistRun || "-";
        const hfo = this.displayHfo || "-";
        const mgo = this.displayMgo || "-";
        const lfo = this.displayLfo || "-";
        const overconsumption = this.displayOverconsumption || "-";
        const completeness = this.displayCompleteness || "-";

        const fouling =
          this.displayFouling || this.displayFouling === 0
            ? `${this.formatNumber(this.displayFouling, 2)}%`
            : "-";

        const ciiValue =
          this.ciiHistory && this.ciiHistory.length > 0
            ? this.formatNumber(this.ciiHistory[this.ciiHistory.length - 1].cii,2)
            : "-";

        const ladenRows = buildKdRows(ladenResult);
        const ballastRows = buildKdRows(ballastResult);

        const ladenDraft = ladenProfile
          ? this.formatNumber(ladenProfile.draft, 2)
          : "-";

        const ladenTrim = ladenProfile
          ? this.formatNumber(ladenProfile.trim, 2)
          : "-";

        const ballastDraft = ballastProfile
          ? this.formatNumber(ballastProfile.draft, 2)
          : "-";

        const ballastTrim = ballastProfile
          ? this.formatNumber(ballastProfile.trim, 2)
          : "-";

        const reportData = {
          reportTitle,
          reportSubject,
          reportAuthor,
          today,
          vessel,

          ciiValue,
          startDate,
          stopDate,
          departurePort,
          arrivalPort,
          departureTime,
          totalDistRun,

          hfoLabel: this.hfoLabel,
          lfoLabel: this.lfoLabel,
          hfo,
          mgo,
          lfo,

          avg_wind_speed: avgWindSpeed,
          avg_wave_height: avgWaveHeight,
          gen_weather_desc: genWeatherDesc,

          overconsumption,
          completeness,
          fouling,

          ladenDraft,
          ladenTrim,
          ladenRows,

          ballastDraft,
          ballastTrim,
          ballastRows,

          lastCleaningDate: this.getLatestCleaningDate(),
          sensorIssues: this.getSensorIssues(),

          voyageNum,
          voyageLeg,
        };

        const docDefinition = buildPerformanceReportDocDefinition(reportData);

        const filename = [
          this.safeFilenameValue(vessel),
          `voyage-${this.safeFilenameValue(voyageNum)}`,
          `leg-${this.safeFilenameValue(voyageLeg)}`,
          "performance-report",
          today,
        ].join("-");

        pdfMake.createPdf(docDefinition).download(`${filename}.pdf`);

      } catch (error) {
        console.error("Error exporting PDF:", error);
      } finally {
        this.isExporting = false;
      }
    },
  },
};
</script>
